import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { CustomPropertyRow } from '../CustomPropertyRow';
import { FontPicker } from '../components/FontPicker';
import { IFont, getFontFromTextStyle } from '../../data/IFont';
import { TextAlignmentSelect } from '../components/TextAlignmentSelect';
import { TextAlignment } from '../../data/TextAlignment';
import { MultiColorRow } from '../components/MultiColorRow';
import { PropertyType } from '@beatflyer/shaders-lib';
import { CustomProperty } from '../../data/CustomProperty';
import { TextTransform } from 'data/TextTransform';
import {
  CaptionsDisplayMode,
  CAPTIONS_DISPLAY_MODE_LABELS
} from 'captions/CaptionsDisplayMode';
import { isPickingColor } from 'utils';
import {
  FrameSettingsPopover,
  FrameSettingsPopoverProps
} from './FrameSettingsPopover';
import { TextStyleUpdate } from 'data/TextStyleUpdate';

const GRADIENT_DIRECTION_HORIZONTAL = 'horizontal';
const GRADIENT_DIRECTION_VERTICAL = 'vertical';
const GRADIENT_DIRECTIONS = [
  GRADIENT_DIRECTION_VERTICAL,
  GRADIENT_DIRECTION_HORIZONTAL
];

interface Props extends FrameSettingsPopoverProps {
  text: string;
  textStyle: PIXI.TextStyle;
  isCaptions?: boolean;
  hasCaptions?: boolean;
  onChangeText: (value: string) => void;
  onStyleChange: (property: string, change: TextStyleUpdate) => void;
  onEditCaptions?: () => void;
  captionsDisplayMode?: CaptionsDisplayMode;
  captionsAnimated?: boolean;
  captionsHaveBackground?: boolean;
  captionsBackgroundColor?: string;
  onCaptionsHaveBackgroundChange?: (value: boolean) => void;
  onCaptionsBackgroundChange?: (color: string) => void;
  onDisplayModeChange?: (mode: CaptionsDisplayMode) => void;
  onCaptionAnimationChange?: (value: boolean) => void;
}

class TextSettingsPopover extends React.Component<Props> {
  lastStrokeThikness = 10;
  textareaEl = React.createRef<HTMLTextAreaElement>();

  componentDidMount() {
    this.textareaEl.current?.select();
  }

  didClickOnWindow = (e: MouseEvent) => {
    if (isPickingColor()) {
      // is picking color
      return;
    }
    const me = ReactDOM.findDOMNode(this) as Element;
    if (!me.contains(e.target as HTMLElement)) {
      this.props.onClose();
    }
  };

  onPropertyChange = (property: string, value: any) => {
    const {
      textStyle,
      onStyleChange,
      onDisplayModeChange,
      onCaptionAnimationChange,
      onCaptionsHaveBackgroundChange,
      onCaptionsBackgroundChange
    } = this.props;
    switch (property) {
      case 'presentation':
        const mode = value as CaptionsDisplayMode;
        onDisplayModeChange!(mode);
        break;
      case 'animated':
        const animated = value as boolean;
        onCaptionAnimationChange!(animated);
        break;
      case 'uppercase':
        onStyleChange(property, {
          textTransform: (value as boolean)
            ? TextTransform.Uppercase
            : undefined
        });
        break;
      case 'font':
        const font = value as IFont;
        const comp = font.family.split(':');
        onStyleChange(property, {
          fontFamily: comp[0],
          fontStyle: comp.length > 1 ? comp[1] : 'normal',
          fontWeight: font.weight + ''
        });
        break;
      case 'alignment':
        onStyleChange(property, {
          align: value[0] as TextAlignment,
          verticalAlign: value[1] as TextAlignment
        });
        break;
      case 'font size':
        const ratio = textStyle.lineHeight / (textStyle.fontSize as number);
        onStyleChange(property, {
          fontSize: value,
          lineHeight: value * ratio
        });
        break;
      case 'color':
        onStyleChange(property, { fill: value });
        break;
      case 'line height':
        onStyleChange(property, { lineHeight: value });
        break;
      case 'letter space':
        onStyleChange(property, { letterSpacing: value });
        break;
      case 'background':
        if (onCaptionsHaveBackgroundChange) {
          onCaptionsHaveBackgroundChange(value as boolean);
        }
        break;
      case 'background color':
        if (onCaptionsBackgroundChange) {
          onCaptionsBackgroundChange(value as string);
        }
        break;
      case 'gradient dir.':
        onStyleChange(property, {
          fillGradientType: GRADIENT_DIRECTIONS.indexOf(value)
        });
        break;
      case 'shadow':
        onStyleChange(property, { dropShadow: value });
        break;
      case 'shadow color':
        onStyleChange(property, { dropShadowColor: value });
        break;
      case 'shadow dir.':
        onStyleChange(property, {
          dropShadowAngle: (((value as number) - 90) * Math.PI) / 180
        });
        break;
      case 'shadow dist.':
        onStyleChange(property, { dropShadowDistance: value });
        break;
      case 'shadow blur':
        onStyleChange(property, { dropShadowBlur: value });
        break;
      case 'stroke': {
        const show = value as boolean;
        let strokeThickness = 0;
        if (show) {
          strokeThickness = this.lastStrokeThikness;
        } else {
          this.lastStrokeThikness = textStyle.strokeThickness;
        }
        onStyleChange(property, { strokeThickness });
        break;
      }
      case 'stroke color':
        onStyleChange(property, { stroke: value });
        break;
      case 'stroke thick.':
        onStyleChange(property, { strokeThickness: value });
        break;

      default:
        break;
    }
  };

  onColorChanged = (index: number, newColor: string) => {
    if (Array.isArray(this.props.textStyle.fill)) {
      const colors = (this.props.textStyle.fill as string[]).concat([]);
      colors.splice(index, 1, newColor);
      this.props.onStyleChange('color', { fill: colors });
    } else {
      this.props.onStyleChange('color', { fill: newColor });
    }
  };

  addColor = () => {
    let newColors: string[] = [];
    if (Array.isArray(this.props.textStyle.fill)) {
      const colors = this.props.textStyle.fill as string[];
      const lastColor = colors[colors.length - 1];
      newColors = colors.concat([lastColor]);
    } else {
      const currentColor = this.props.textStyle.fill as string;
      newColors = [currentColor, currentColor];
    }
    this.props.onStyleChange('color', { fill: newColors });
  };

  removeColor = (index: number) => {
    if (!Array.isArray(this.props.textStyle.fill)) {
      return;
    }
    const colors = (this.props.textStyle.fill as string[]).concat([]);
    colors.splice(index, 1);
    const fill = colors.length === 1 ? colors[0] : colors;
    this.props.onStyleChange('color', { fill });
  };

  render() {
    const {
      text,
      textStyle,
      isCaptions,
      hasCaptions,
      captionsDisplayMode,
      captionsAnimated,
      captionsHaveBackground,
      captionsBackgroundColor,
      trigger,
      onClose
    } = this.props;
    let properties: CustomProperty[] = [];

    if (isCaptions) {
      properties.push({
        name: 'text',
        type: 'button',
        value: `${hasCaptions ? 'Edit' : 'Create'} captions`
      });
      properties.push({
        name: 'presentation',
        type: PropertyType.SELECT,
        value: captionsDisplayMode!,
        options: [
          CaptionsDisplayMode.AllVisible,
          CaptionsDisplayMode.ComposeAll,
          CaptionsDisplayMode.Pages,
          CaptionsDisplayMode.ComposePages
        ],
        formatValue: (value: any): string => {
          return CAPTIONS_DISPLAY_MODE_LABELS[value];
        }
      });
      if (
        [
          CaptionsDisplayMode.ComposeAll,
          CaptionsDisplayMode.ComposePages
        ].includes(captionsDisplayMode!)
      ) {
        properties.push({
          name: 'animated',
          type: PropertyType.BOOLEAN,
          value: captionsAnimated
        });
      }
    } else {
      properties.push({
        name: 'text',
        type: 'text',
        value: text
      });
    }

    properties.push({
      name: 'font',
      type: 'font',
      value: getFontFromTextStyle(textStyle)
    });

    if (isCaptions) {
      properties.push({
        name: 'uppercase',
        type: PropertyType.BOOLEAN,
        value: (textStyle as any).textTransform === TextTransform.Uppercase
      });
    }

    properties = [
      ...properties,
      {
        name: 'font size',
        type: PropertyType.INT,
        minValue: 4,
        maxValue: 1000,
        value: textStyle.fontSize
      },
      {
        name: 'alignment',
        type: 'alignment',
        value: [textStyle.align, (textStyle as any).verticalAlign]
      },
      {
        name: 'line height',
        type: PropertyType.INT,
        minValue: 4,
        maxValue: 1000,
        value: textStyle.lineHeight
      },
      {
        name: 'letter space',
        type: PropertyType.INT,
        minValue: -30,
        maxValue: 200,
        value: textStyle.letterSpacing
      },
      {
        name: 'color',
        type: PropertyType.COLOR,
        value: textStyle.fill
      }
    ];

    if (Array.isArray(textStyle.fill)) {
      properties.push({
        name: 'gradient dir.',
        type: PropertyType.SELECT,
        value: GRADIENT_DIRECTIONS[textStyle.fillGradientType],
        options: [GRADIENT_DIRECTION_HORIZONTAL, GRADIENT_DIRECTION_VERTICAL]
      });
    }

    if (isCaptions) {
      properties.push({
        name: 'background',
        type: PropertyType.BOOLEAN,
        value: captionsHaveBackground
      });
    }

    if (captionsHaveBackground) {
      properties.push({
        name: 'background color',
        type: PropertyType.COLOR_WITH_ALPHA,
        value: captionsBackgroundColor
      });
    }

    properties.push({
      name: 'shadow',
      type: PropertyType.BOOLEAN,
      value: textStyle.dropShadow
    });

    if (textStyle.dropShadow) {
      properties = [
        ...properties,
        {
          name: 'shadow color',
          type: PropertyType.COLOR,
          value: textStyle.dropShadowColor
        },
        {
          name: 'shadow dir.',
          type: PropertyType.DIRECTION,
          value: textStyle.dropShadowAngle * (180 / Math.PI) + 90
        },
        {
          name: 'shadow dist.',
          type: PropertyType.INT,
          value: textStyle.dropShadowDistance,
          minValue: 0,
          maxValue: 100
        },
        {
          name: 'shadow blur',
          type: PropertyType.INT,
          value: textStyle.dropShadowBlur,
          minValue: 0,
          maxValue: 100
        }
      ];
    }

    properties.push({
      name: 'stroke',
      type: PropertyType.BOOLEAN,
      value: textStyle.strokeThickness > 0
    });

    if (textStyle.strokeThickness > 0) {
      properties = [
        ...properties,
        {
          name: 'stroke color',
          type: PropertyType.COLOR,
          value: textStyle.stroke
        },
        {
          name: 'stroke thick.',
          type: PropertyType.INT,
          value: textStyle.strokeThickness,
          minValue: 1,
          maxValue: 100
        }
      ];
    }

    return (
      <FrameSettingsPopover
        className="text-settings-popover"
        trigger={trigger}
        onClose={onClose}
      >
        {properties.map((p, index) => {
          switch (p.type) {
            case 'text':
              return (
                <div className="custom-property" key={p.name}>
                  <label>{p.name}</label>
                  <textarea
                    value={p.value}
                    ref={this.textareaEl}
                    onChange={e => this.props.onChangeText(e.target.value)}
                  />
                </div>
              );
            case 'button':
              return (
                <div className="custom-property" key={p.name}>
                  <label>{p.name}</label>
                  <button onClick={this.props.onEditCaptions}>{p.value}</button>
                </div>
              );

            case 'font':
              return (
                <div className="custom-property" key={p.name}>
                  <label>{p.name}</label>
                  <FontPicker
                    name={p.name}
                    value={p.value as IFont}
                    onChange={this.onPropertyChange}
                  />
                </div>
              );

            case 'alignment':
              return (
                <div className="custom-property" key={p.name}>
                  <label>{p.name}</label>
                  <TextAlignmentSelect
                    horizontalAlignment={p.value[0]}
                    verticalAlignment={p.value[1]}
                    onChange={this.onPropertyChange}
                  />
                </div>
              );

            default:
              if (p.name === 'color') {
                return (
                  <MultiColorRow
                    key={p.name}
                    color={p.value as string | string[]}
                    onChange={this.onColorChanged}
                    addColor={this.addColor}
                    removeColor={this.removeColor}
                    compact={true}
                  />
                );
              }

              return (
                <CustomPropertyRow
                  name={p.name}
                  id={p.name}
                  key={p.name}
                  value={p.value}
                  data={p}
                  onChange={this.onPropertyChange}
                />
              );
          }
        })}
      </FrameSettingsPopover>
    );
  }
}

export { TextSettingsPopover };



// WEBPACK FOOTER //
// ./src/controller/settings-popovers/TextSettingsPopover.tsx